<template>
  <div class="py-6 px-5 sm:p-8 bg-light w-screen md:max-w-md h-screen md:h-auto flex flex-col justify-center">
    <h2 class="flex font-semibold text-lg md:text-xl text-bolder mb-2">
      {{ $t('text-your-catalogue-rating') }}: {{ localized(store.content, 'name') }}
    </h2>
    <hr class="mb-2 md:mb-4" >
    <div class="w-full flex justify-center mb-5 whitespace-nowrap">
      <component
        :is="value <= score ? StarSolid : StarIcon"
        v-for="value in 5"
        :key="value"
        class="w-7 h-7 text-orange hover:text-orange-hover cursor-pointer"
        @click="score = value"
      />
    </div>
    <div class="relative flex flex-col sm:flex-row justify-between w-full">
      <TextArea
        v-model="review"
        :label="$t('text-review')"
        name="catalogue-rating-review"
        type="text"
        variant="outline"
        :rows="windowSize?.width?.value < 1024 ? 4 : 8"
        class-name="w-full mb-5"
        @cursor-changed="cursorPosition = $event"
      />
      <ChatEmojiPicker
        v-model="review"
        class="bottom-6 end-3"
        :cursor-position="cursorPosition"
        panel-offset="right-0 bottom-5"
        @cursor-changed="cursorPosition = $event"
      />
    </div>
    <div class="flex flex-col sm:flex-row justify-end w-full">
      <Button
        class="ms-auto"
        :disabled="loading || !score"
        :loading="loading"
        :on-click="submit"
      >
        {{ $t('text-submit') }}
      </Button>
    </div>
  </div>
</template>

<script setup>
import { useSelectedStore } from '@stores/selected';
import TextArea from "@components/ui/text-area";
import Button from "@components/ui/button";
import StarIcon from "@components/icons/outline/star-icon";
import StarSolid from "@components/icons/solid/star-solid";
import ChatEmojiPicker from "@components/chat/chat-emoji-picker";

const props = defineProps({
  update: {
    type: Boolean,
    default: false
  }
});

const store = useSelectedStore();
const { t } = useI18n();
const { $eventBus, $toast } = useNuxtApp();
const windowSize = useWindowSize();
const loading = ref(false);
const score = ref(store.score || store.rating?.score || 0);
const review = ref(store.rating?.review || '');
const cursorPosition = ref(0);

const handleClose = () => {
  $eventBus.emit('modal:close');
  if (store.action === 'RETURN_TO_CATALOGUE_MODAL') {
    $eventBus.emit('modal:open', 'CATALOGUE_DETAILS');
    store.setAction(null);
  }
};

const submit = async () => {
  if (loading.value || !score.value) {
    return;
  }

  loading.value = true;
  const result = await cataloguePostRating(store.content?.id, {
    score: score.value,
    review: review.value
  }, props.update);

  if (!result) {
    $toast.error(t(props.update ? 'error.catalogue.rating.update' : 'error.catalogue.rating.create'));
    loading.value = false;
    return;
  }

  const currentRatings = store.content?.ratings || [];
  const ratings = props.update
    ? currentRatings.map((rating) => rating.isOwn ? result : rating)
    : [result, ...currentRatings];
  const ratingScore = ratings.length
    ? ratings.reduce((total, rating) => total + rating.score, 0) / ratings.length
    : 0;

  $eventBus.emit('catalogue:update', {
    id: store.content.id,
    ratings,
    ratingCount: ratings.length,
    ratingScore
  });
  store.setContent({
    ...store.content,
    ratings,
    ratingCount: ratings.length,
    ratingScore
  });
  store.setRating(result);
  store.setScore(result.score);
  $toast.success(t(props.update ? 'success.catalogue.rating.update' : 'success.catalogue.rating.create'));
  handleClose();
  loading.value = false;
};
</script>
